import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { DEFAULT_FILE_ORDERING_PRIORITY, FILE_ORDERING_SETTING_KEY } from 'citadel-shared/fileOrdering.js'

import axios from '../../dataClient/dataClient'
import { allSettingsSections } from '../../routes/settingsRoutes'
import { theme } from '../../themes/theme'
import { FlexColumn, FlexEndContainer, FlexRow, LightBGContainer, SpaceBetweenRowContainer, VerticallyCenteredFlexColumn } from '../styles/Containers'
import { FormTitle, MedBodyText, SmallBodyText } from '../styles/Text'
import { MUTED_TEXT } from '../styles/colors'
import Button, { SecondaryButton } from '../common/Button'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import { ErrorMessage, SuccessMessage, WarningMessage } from '../common/UserMessage'
import Breadcrumbs from '../settings/Breadcrumbs'
import FileOrderingPriorityList from '../settings/FileOrderingPriorityList'

const HeaderRow = styled(SpaceBetweenRowContainer)`
  min-height: 37px;
  align-items: center;
`

const Card = styled(LightBGContainer)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  box-sizing: border-box;
`

const CardTitle = styled(FormTitle)`
  font-weight: 600;
`

const HelperRow = styled(FlexRow)`
  gap: 8px;
  align-items: flex-start;
  color: ${MUTED_TEXT};

  .MuiSvgIcon-root {
    width: 16px;
    height: 16px;
    margin-top: 1px;
    flex-shrink: 0;
  }
`

const MutedText = styled(SmallBodyText)`
  color: ${MUTED_TEXT};
`

const FooterRow = styled(SpaceBetweenRowContainer)`
  align-items: center;
  gap: 20px;
`

const StatusSlot = styled.div`
  flex: 1;
  min-width: 0;
`

const Actions = styled(FlexEndContainer)`
  flex-shrink: 0;
  white-space: nowrap;
`

const SaveSpinner = styled(CircularProgress)`
  color: ${theme.colors.primaryButton.text};
  margin-right: 4px;
`

const SETTING_URL = `/settings/${FILE_ORDERING_SETTING_KEY}`

function samePriority(a, b) {
  return a.length === b.length && a.every((tier, index) => tier === b[index])
}

// Defer route access to avoid the settings route import cycle.
function getCrumbs() {
  const { files } = allSettingsSections
  return [
    { label: files.label, path: files.path },
    { label: files.subsections.fileOrdering.label, path: files.subsections.fileOrdering.path }
  ]
}

function SettingsFileOrderingPage() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savedPriority, setSavedPriority] = useState([...DEFAULT_FILE_ORDERING_PRIORITY])
  const [priority, setPriority] = useState([...DEFAULT_FILE_ORDERING_PRIORITY])
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState('idle') // idle | saved | savedReorderFailed | error

  const crumbs = getCrumbs()
  const isDirty = !samePriority(priority, savedPriority)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    axios
      .get(SETTING_URL)
      .then(({ data }) => {
        if (cancelled) return
        const stored = data.data.value.priority
        setSavedPriority(stored)
        setPriority(stored)
      })
      .catch(e => {
        if (cancelled) return
        console.error('Failed to retrieve the file ordering setting', e)
        setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  function handleChange(nextPriority) {
    setPriority(nextPriority)
    setSaveStatus('idle')
  }

  function handleReset() {
    handleChange([...DEFAULT_FILE_ORDERING_PRIORITY])
  }

  function handleSave() {
    if (saving || !isDirty) return
    setSaving(true)
    setSaveStatus('idle')
    axios
      .put(SETTING_URL, { value: { priority } })
      .then(({ data }) => {
        const stored = data.data.value.priority
        setSavedPriority(stored)
        setPriority(stored)
        // The saved setting remains valid even if immediate reordering fails.
        if (data.data.afterSaveError) {
          console.error('File ordering saved but the re-order of open requests failed', data.data.afterSaveError)
          setSaveStatus('savedReorderFailed')
        } else {
          setSaveStatus('saved')
        }
      })
      .catch(e => {
        console.error('Failed to save the file ordering setting', e)
        setSaveStatus('error')
      })
      .finally(() => setSaving(false))
  }

  if (loading) {
    return (
      <FlexColumn height="100%" width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={crumbs} />
        </HeaderRow>
        <Loading>Loading file ordering setting...</Loading>
      </FlexColumn>
    )
  }

  if (loadError) {
    return (
      <FlexColumn width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={crumbs} />
        </HeaderRow>
        <VerticallyCenteredFlexColumn height="100%">
          <LoadingError message={['There was an error loading the file ordering setting. Please refresh the page to try again.']} />
        </VerticallyCenteredFlexColumn>
      </FlexColumn>
    )
  }

  let status = null
  if (saveStatus === 'saved') status = <SuccessMessage>Saved. Categories that are not ordered manually will use the new priority.</SuccessMessage>
  else if (saveStatus === 'savedReorderFailed')
    status = (
      <WarningMessage>
        <MedBodyText>Saved. Re-ordering open requests did not finish — they will catch up on their next convert or combine.</MedBodyText>
      </WarningMessage>
    )
  else if (saveStatus === 'error') status = <ErrorMessage>Hm, an error occurred while saving. Your changes are still here — try again.</ErrorMessage>
  else if (isDirty && !saving) status = <MutedText>Unsaved changes</MutedText>

  return (
    <FlexColumn width="100%" gap="10px">
      <HeaderRow>
        <Breadcrumbs crumbs={crumbs} />
      </HeaderRow>
      <Card padding="25px 30px">
        <FlexColumn gap="5px">
          <CardTitle>Date priority for file ordering</CardTitle>
          <MedBodyText>Files in a category are ordered by date before combining. Drag a source to change which date is checked first.</MedBodyText>
        </FlexColumn>
        <FileOrderingPriorityList priority={priority} onChange={handleChange} disabled={saving} />
        <HelperRow>
          <InfoOutlinedIcon />
          <MutedText>
            The first source with a date wins, in this order. Files with no date in any source fall back to the upload date. Changing the priority
            re‑orders open requests that have not been ordered manually.
          </MutedText>
        </HelperRow>
        <FooterRow>
          <StatusSlot>{status}</StatusSlot>
          <Actions gap="10px">
            <SecondaryButton onClick={handleReset} disabled={saving}>
              Reset to default
            </SecondaryButton>
            <Button onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? (
                <>
                  <SaveSpinner size={16} thickness={4} /> Saving
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Actions>
        </FooterRow>
      </Card>
    </FlexColumn>
  )
}

export default SettingsFileOrderingPage
